import { isAttemptedOutcome } from '../practice/practiceProgress';
import { findNumberedQuestion } from '../practice/voiceCommands';

export const MAX_DAILY_QUESTIONS = 100;
export const trackerDayPath = (paperId, date) => ['papers', paperId, 'iqTrackerDays', date];
export const trackerProgressPath = (paperId, date, userId) => [...trackerDayPath(paperId, date), 'progress', userId];
export function localDate(date = new Date()) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
export function isCalendarDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false;
    const parsed = new Date(`${value}T12:00:00`);
    return !Number.isNaN(parsed.getTime()) && localDate(parsed) === value;
}
export const displayDate = date => isCalendarDate(date) ? new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' }) : '';
export function canAccessTracker(profile, paperId) {
    if (profile?.role === 'superadmin') return true;
    if (profile?.role === 'student') return profile.assignedPapers?.includes(paperId) === true;
    if (profile?.role !== 'admin') return false;
    const contentPermission = !profile.allowedFeatures || profile.allowedFeatures.includes('manage_exams') || profile.allowedFeatures.includes('view_reports');
    return contentPermission && (!profile.assignedPapers ? !profile.allowedFeatures : profile.assignedPapers.includes(paperId));
}
export const canManageTracker = (profile, paperId) => ['admin', 'superadmin'].includes(profile?.role) && canAccessTracker(profile, paperId) && (profile.role === 'superadmin' || !profile.allowedFeatures || profile.allowedFeatures.includes('manage_exams'));
export const canViewTrackerReports = (profile,paperId) => ['admin','superadmin'].includes(profile?.role) && canAccessTracker(profile,paperId);
export function orderedDailyQuestions(day, questions) {
    const byId = new Map(questions.filter(q => q.status !== 'draft' && !q.contentUnavailable && q.recordKind !== 'scenario').map(q => [q.id, q]));
    const ids = [...new Set(Array.isArray(day?.questionIds) ? day.questionIds : [])];
    return { questions: ids.map(id => byId.get(id)).filter(Boolean), missingIds: ids.filter(id => !byId.has(id)), ids };
}
export function resolveQuestionNumbers(text, questions) {
    const tokens = text.trim().split(/[\s,;]+/).filter(Boolean);
    if (!tokens.length) throw new Error('Enter question numbers, for example 1, 11, 20.');
    const numbers = [];
    for (const token of tokens) {
        const match = /^(?:q)?([1-9]\d*)(?:-([1-9]\d*))?$/i.exec(token);
        if (!match) throw new Error(`“${token}” is not a question number. Use 1, 11, 20 or 1-5.`);
        const first = Number(match[1]), last = Number(match[2] || match[1]);
        if (!Number.isSafeInteger(last) || last < first || last - first >= MAX_DAILY_QUESTIONS) throw new Error('Use an ascending range of up to 100 questions.');
        for (let number = first; number <= last; number++) {
            if (!numbers.includes(number)) numbers.push(number);
            if (numbers.length > MAX_DAILY_QUESTIONS) throw new Error('A day can contain up to 100 questions.');
        }
    }
    return numbers.map(number => {
        const result = findNumberedQuestion(questions, number);
        if (result.error) throw new Error(result.error);
        return result.question.id;
    });
}
export const dailyQuestionDone = (progress, id) => isAttemptedOutcome(progress?.results?.[id]?.outcome);
export function dailySummary(ids, progress) {
    const unique = [...new Set(ids)];
    return { total: unique.length, completed: unique.filter(id => dailyQuestionDone(progress, id)).length,
        correct: unique.filter(id => progress?.results?.[id]?.outcome === 'correct').length,
        revisit: unique.filter(id => progress?.results?.[id]?.outcome === 'incorrect').length };
}
// Count real questions once across the paper, regardless of how many dates include them.
// Never use a stored aggregate: old totals may include deleted questions or repeat attempts.
export function paperProgressSummary(questions, progress = {}) {
    const unique = [...new Map(questions.filter(q => q.id && q.status !== 'draft' && q.recordKind !== 'scenario').map(q => [q.id, q])).values()];
    const completed = new Set(Array.isArray(progress.completedQuestions) ? progress.completedQuestions : []);
    const items = unique.map(question => {
        const result = progress.results?.[question.id]?.outcome;
        const outcome = isAttemptedOutcome(result) ? result : completed.has(question.id) ? 'previous' : 'unattempted';
        return { question, outcome, attempted: outcome !== 'unattempted' };
    });
    const attempted = items.filter(item => item.attempted).length;
    return { items, total: items.length, attempted, remaining: items.length - attempted,
        correct: items.filter(item => item.outcome === 'correct').length,
        revisit: items.filter(item => item.outcome === 'incorrect').length,
        reviewed: items.filter(item => item.outcome === 'reviewed').length,
        awaitingReview: items.filter(item => item.outcome === 'review').length,
        previous: items.filter(item => item.outcome === 'previous').length,
        percent: items.length ? Math.round(attempted / items.length * 100) : 0 };
}
export function dailyPracticeUrl(paperId, date, questionId) {
    return `/iq-practice/${encodeURIComponent(paperId)}/day/${date}?${new URLSearchParams({ question: questionId })}`;
}
export function validateDayPlan(plan, questions) {
    if (!isCalendarDate(plan.date)) throw new Error('Choose a valid date.');
    if (!plan.title.trim() || plan.title.length > 120) throw new Error('Add a title of up to 120 characters.');
    if (plan.instructions.length > 2000) throw new Error('Keep instructions under 2,000 characters.');
    if (!Array.isArray(plan.questionIds) || !plan.questionIds.length || plan.questionIds.length > MAX_DAILY_QUESTIONS) throw new Error('Choose between 1 and 100 IQ questions.');
    if (new Set(plan.questionIds).size !== plan.questionIds.length) throw new Error('Each question can appear once in a daily plan.');
    if (orderedDailyQuestions(plan, questions).missingIds.length) throw new Error('Remove unavailable questions before saving the plan.');
}
